import { can } from '../utils/memberPermissions';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  MessageCircle,
  Calendar,
  Users,
  Settings,
  Building2,
  Package
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import InstallApp from './InstallApp';

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const mainNavItems = [
    { path: '/', label: 'Today', icon: Calendar },
    ...((user?.whatsappInboxEnabled || user?.emailInboxEnabled) ? [{ path: '/inbox', label: 'Inbox', icon: MessageCircle }] : []),
    { path: '/leads', label: 'Enquiries', icon: Users },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/organisations', label: 'Organisations', icon: Building2 },
    { path: '/catalogue', label: 'Catalogue', icon: Package },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen bg-white border-r border-gray-200 fixed left-0 top-0 z-40">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-gray-200">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={`${process.env.PUBLIC_URL}/leadmarka_logo-color.svg`}
            alt="LeadMarka"
            className="w-8 h-8"
          />
          <span className="font-bold text-xl text-gray-900">LeadMarka</span>
        </Link>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        {/* Primary Navigation */}
        <nav aria-label="Workspace navigation" className="px-3 space-y-1">
          {mainNavItems.filter(item => item.path !== '/reports' || can(user, 'viewReports')).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <div key={item.path}>
                {['/', '/contacts', '/catalogue'].includes(item.path) && <p className="px-3 pb-2 pt-5 text-xs font-semibold uppercase tracking-wider text-gray-500">{item.path === '/' ? 'Work' : item.path === '/contacts' ? 'People' : 'Business'}</p>}
                <Link
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span>{item.label}</span>
                </Link>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section - User & Settings */}
      <div className="border-t border-gray-200 p-3">
        {/* Workspace Info */}
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.workspaceCompanyName || user?.businessName || 'Workspace'}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>

        <InstallApp />

        {/* Settings Link */}
        <Link
          to="/settings"
          aria-current={isActive('/settings') ? 'page' : undefined}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            isActive('/settings')
              ? 'bg-primary-50 text-primary-700'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Settings className={`w-5 h-5 ${isActive('/settings') ? 'text-primary-600' : 'text-gray-500'}`} />
          <span>Settings</span>
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;
